import "./styles.css";
import { initializeEmailCopyButtons } from "./copy-email";
import { initializeLocale } from "./localization";
import { loginEntryUrl, trialEntryUrl } from "./site-config";

if ("scrollRestoration" in window.history) {
  window.history.scrollRestoration = "manual";
}

const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
const headerNavLinks = Array.from(document.querySelectorAll<HTMLAnchorElement>(".site-nav a, .brand"));
const overviewDemo = document.querySelector<HTMLElement>(".overview-screen > .product-demo-frame");

document.querySelectorAll<HTMLAnchorElement>("[data-trial-link]").forEach((link) => {
  link.href = trialEntryUrl;
});

document.querySelectorAll<HTMLAnchorElement>("[data-login-link]").forEach((link) => {
  link.href = loginEntryUrl;
});

initializeLocale();

const caseTabs = Array.from(document.querySelectorAll<HTMLButtonElement>("[data-case-tab]:not([data-case-disabled])"));
const casePanels = Array.from(document.querySelectorAll<HTMLElement>("[data-case-panel]:not([data-case-disabled])"));

if (caseTabs.length && casePanels.length) {
  const caseIds = caseTabs
    .map((tab) => tab.dataset.caseTab)
    .filter((caseId): caseId is string => Boolean(caseId));

  const selectCase = (caseId: string, updateUrl = false) => {
    if (!caseIds.includes(caseId)) return;

    caseTabs.forEach((tab) => {
      const isSelected = tab.dataset.caseTab === caseId;
      tab.classList.toggle("is-active", isSelected);
      tab.setAttribute("aria-selected", String(isSelected));
      tab.tabIndex = isSelected ? 0 : -1;
    });

    casePanels.forEach((panel) => {
      const isSelected = panel.dataset.casePanel === caseId;
      panel.hidden = !isSelected;
      panel.classList.remove("is-entering");
      if (isSelected && updateUrl && !prefersReducedMotion) {
        void panel.offsetWidth;
        panel.classList.add("is-entering");
      }
    });

    document.body.dataset.caseTheme = caseId;

    document.querySelectorAll<HTMLAnchorElement>("[data-locale-switch]").forEach((link) => {
      const target = new URL(link.href, window.location.origin);
      if (caseId === "oqq") {
        target.searchParams.delete("case");
      } else {
        target.searchParams.set("case", caseId);
      }
      link.href = `${target.pathname}${target.search}${target.hash}`;
    });

    if (updateUrl) {
      const target = new URL(window.location.href);
      if (caseId === "oqq") {
        target.searchParams.delete("case");
      } else {
        target.searchParams.set("case", caseId);
      }
      if (target.hash && !target.hash.startsWith(`#${caseId}-`)) target.hash = "";
      window.history.replaceState(null, "", `${target.pathname}${target.search}${target.hash}`);
    }
  };

  const requestedCase = new URLSearchParams(window.location.search).get("case");
  selectCase(requestedCase && caseIds.includes(requestedCase) ? requestedCase : caseIds[0]);

  caseTabs.forEach((tab, index) => {
    tab.addEventListener("click", () => {
      const caseId = tab.dataset.caseTab;
      if (caseId) selectCase(caseId, true);
    });

    tab.addEventListener("keydown", (event) => {
      if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
      event.preventDefault();
      const offset = event.key === "ArrowRight" ? 1 : -1;
      const nextIndex = (index + offset + caseTabs.length) % caseTabs.length;
      const nextTab = caseTabs[nextIndex];
      const caseId = nextTab.dataset.caseTab;
      if (!caseId) return;
      selectCase(caseId, true);
      nextTab.focus();
    });
  });
}

initializeEmailCopyButtons();

const revealOverviewDemo = () => {
  if (!overviewDemo) return;
  document.documentElement.classList.add("is-overview-demo-revealed");
  window.removeEventListener("scroll", revealOverviewDemoOnScroll);
};

const revealOverviewDemoOnScroll = () => {
  if (window.scrollY > 120) {
    revealOverviewDemo();
  }
};

const jumpToHashTarget = (hash: string) => {
  if (hash === "#overview") {
    document.documentElement.classList.add("is-jump-scrolling");
    window.scrollTo(0, 0);
    window.setTimeout(() => {
      document.documentElement.classList.remove("is-jump-scrolling");
    }, 80);
    return true;
  }

  const target = document.querySelector<HTMLElement>(hash);
  if (!target) return false;

  if (hash === "#product-loop") {
    revealOverviewDemo();
  }

  document.documentElement.classList.add("is-jump-scrolling");
  const targetTop = target.getBoundingClientRect().top + window.scrollY - 88;
  window.scrollTo(0, Math.max(0, targetTop));
  window.setTimeout(() => {
    document.documentElement.classList.remove("is-jump-scrolling");
  }, 80);
  return true;
};

const initializePagePosition = () => {
  const hash = window.location.hash;
  if (hash) {
    jumpToHashTarget(hash);
  }
  if (hash && hash !== "#overview") {
    revealOverviewDemo();
  }
};

headerNavLinks.forEach((link) => {
  const href = link.getAttribute("href");
  if (!href) return;

  if (href === "/" && window.location.pathname === "/") {
    link.addEventListener("click", (event) => {
      event.preventDefault();
      jumpToHashTarget("#overview");
      window.history.replaceState(null, "", window.location.pathname);
    });
    return;
  }

  if (href.startsWith("#")) {
    link.addEventListener("click", (event) => {
      const target = document.querySelector<HTMLElement>(href);
      if (!target) return;

      event.preventDefault();
      jumpToHashTarget(href);
      const nextUrl = href === "#overview" ? window.location.pathname : href;
      window.history.replaceState(null, "", nextUrl);
    });
    return;
  }

});

initializePagePosition();

if (overviewDemo) {
  window.addEventListener("scroll", revealOverviewDemoOnScroll, { passive: true });
}

if (!prefersReducedMotion) {
  const autoScopes = Array.from(document.querySelectorAll<HTMLElement>(".auto-detail"));

  autoScopes.forEach((scope, index) => {
    window.setTimeout(() => {
      scope.classList.add("is-auto-expanded");

      window.setInterval(() => {
        scope.classList.toggle("is-auto-expanded");
      }, 5600 + index * 240);
    }, 900 + index * 520);
  });

  const liveGroups = [
    ".loop-map article",
    ".creator-table .creator-row",
    ".task-table .task-row",
    ".ranking-card .rank-row",
    ".kanban-grid .lane article",
  ];

  liveGroups.forEach((selector, groupIndex) => {
    const items = Array.from(document.querySelectorAll<HTMLElement>(selector));
    if (items.length < 2) return;

    let activeIndex = items.findIndex((item) => item.classList.contains("is-live"));
    if (activeIndex < 0) activeIndex = 0;

    window.setInterval(
      () => {
        items[activeIndex]?.classList.remove("is-live");
        activeIndex = (activeIndex + 1) % items.length;
        items[activeIndex]?.classList.add("is-live");
      },
      2600 + groupIndex * 320,
    );
  });
}

const stepNavLinks = Array.from(document.querySelectorAll<HTMLAnchorElement>(".product-step-nav a"));
const productSections = stepNavLinks
  .map((link) => document.querySelector<HTMLElement>(link.hash))
  .filter((section): section is HTMLElement => Boolean(section));

if (stepNavLinks.length && productSections.length) {
  const setCurrentStep = () => {
    const activeSection =
      productSections
        .map((section) => ({ id: section.id, top: Math.abs(section.getBoundingClientRect().top - 120) }))
        .sort((a, b) => a.top - b.top)[0]?.id ?? productSections[0].id;

    stepNavLinks.forEach((link) => {
      if (link.hash === `#${activeSection}`) {
        link.setAttribute("aria-current", "step");
      } else {
        link.removeAttribute("aria-current");
      }
    });
  };

  setCurrentStep();
  window.addEventListener("scroll", setCurrentStep, { passive: true });
}
